import { CardGroup } from '@ps/isp-web-component-library';
import type { EventId } from '@ps/isp-web-services-types';
import {
  TrackingContext,
  type TrackingContextValue,
  sportsEvent,
  sportsMarket
} from '@ps/web-shared-analytics';
import { Translation } from '@ps/web-shared-components';
import React, { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';

import Redirect from '@swf/components/Redirect/Redirect';
import withWidgetTestId from '@swf/core/hoc/withWidgetTestId';
import { select } from '@swf/core/store';
import type { WidgetFC } from '@swf/core/types/Widget';
import {
  CompetitionCard,
  CompetitionTab,
  CompetitionTabs,
  ExpandablePanel,
  Message,
  Title,
  TryAgainMessage
} from '@swf/sports/components';
import { Breadcrumb } from '@swf/sports/components/Breadcrumb/Breadcrumb';
import useMarketUpdates from '@swf/sports/hooks/useMarketUpdates';
import { EventTypeId } from '@swf/sports/types/eventType';
import { isOddsBoostMarket } from '@swf/sports/utils/isOddsBoostMarket';
import mergeWithTrackingContextValue from '@swf/sports/utils/mergeWithTrackingContextValue';

import { TXTWCL_There_are_no_outrights_availab_ELL } from './SportsCompetitionOutrightsWidget.messages.json';
import styles from './SportsCompetitionOutrightsWidget.scss';
import LoadingState from './components/LoadingState/LoadingState';
import Market from './components/Market/Market';
import {
  sportsCompetitionOutrightsWidgetModel,
  useSportsCompetitionOutrightsWidget
} from './model';
import type { Market as RawMarket } from './types';

const appendTrackingEntities = (
  trackingContext: TrackingContextValue,
  market: RawMarket,
  eventId: EventId,
  eventStartTime: string
): TrackingContextValue =>
  mergeWithTrackingContextValue(trackingContext, [
    sportsEvent({ eventId, eventStartTime: new Date(eventStartTime).getTime() }),
    sportsMarket({
      marketId: market.id,
      marketInPlay: false,
      marketIsOddsBoost: isOddsBoostMarket(market.marketType)
    })
  ]);

const getHiddenTabs = (showEvents = true, showMatches = true): CompetitionTab[] => {
  if (showEvents && showMatches) return [];
  if (showEvents) return [CompetitionTab.MATCHES];
  if (showMatches) return [CompetitionTab.EVENTS];
  return [CompetitionTab.MATCHES, CompetitionTab.EVENTS];
};

const SportsCompetitionOutrightsWidget: WidgetFC = () => {
  const { widgetSelect, widgetDispatch } = useSportsCompetitionOutrightsWidget();
  const competitionName = useSelector(widgetSelect.competitionName);
  const logoUrl = useSelector(widgetSelect.logoUrl);
  const errorCode = useSelector(widgetSelect.errorCode);
  const isLoading = useSelector(widgetSelect.isLoading);
  const outrights = useSelector(widgetSelect.outrights);
  const showEvents = useSelector(widgetSelect.showEvents);
  const showMatches = useSelector(widgetSelect.showMatches);
  const eventTypeName = useSelector(widgetSelect.eventTypeName);
  const { eventTypeSlug, eventTypeId, competitionId, competitionSlug } = useSelector(
    select.sports.getUrlParams
  );

  const ids = useMemo(
    () => (outrights || []).map(({ markets }) => markets.map(({ id }) => id)).flat(),
    [outrights]
  );

  useMarketUpdates(ids, widgetDispatch.marketUpdates);
  const trackingContextValue = useContext(TrackingContext);

  if (isLoading) {
    return <LoadingState />;
  }

  if (errorCode === 404) {
    return (
      <Redirect
        alias={Number(eventTypeId) === EventTypeId.GOLF ? 'sportsGolfMatches' : 'sportsCompetition'}
        values={{ eventTypeId, eventTypeSlug, competitionId, competitionSlug }}
      />
    );
  }

  if (errorCode) {
    return (
      <>
        <Breadcrumb fallbackBreadcrumbText={eventTypeName} />
        {competitionName && <CompetitionCard competitionName={competitionName} logoUrl={logoUrl} />}
        <CompetitionTabs hiddenTabs={getHiddenTabs(showEvents, showMatches)} />
        <TryAgainMessage />
      </>
    );
  }

  if (outrights?.length) {
    return (
      <>
        <Breadcrumb fallbackBreadcrumbText={eventTypeName} />
        {competitionName && <CompetitionCard competitionName={competitionName} logoUrl={logoUrl} />}
        <CompetitionTabs hiddenTabs={getHiddenTabs(showEvents, showMatches)} />
        {outrights.map(({ id, name, markets, eventStartTime }) => {
          if (!markets.length) return null;

          return (
            <div className={styles.wrapper} key={id}>
              <ExpandablePanel isOpenInitially title={<Title>{name}</Title>}>
                <CardGroup isCondensed className={styles.marketsWrapper}>
                  {markets.map((market) => (
                    <TrackingContext.Provider
                      key={market.id}
                      value={appendTrackingEntities(
                        trackingContextValue,
                        market,
                        id,
                        eventStartTime
                      )}
                    >
                      <Market
                        market={market}
                        isOpenInitially={markets.length === 1 ? true : false}
                        eventId={id}
                        eventStartTime={eventStartTime}
                      />
                    </TrackingContext.Provider>
                  ))}
                </CardGroup>
              </ExpandablePanel>
            </div>
          );
        })}
      </>
    );
  }

  return (
    <>
      <Breadcrumb fallbackBreadcrumbText={eventTypeName} />
      {competitionName && <CompetitionCard competitionName={competitionName} logoUrl={logoUrl} />}
      <CompetitionTabs hiddenTabs={getHiddenTabs(showEvents, showMatches)} />
      <Message message={<Translation {...TXTWCL_There_are_no_outrights_availab_ELL} />} />
    </>
  );
};

SportsCompetitionOutrightsWidget.model = sportsCompetitionOutrightsWidgetModel;

export default withWidgetTestId(SportsCompetitionOutrightsWidget, 'sports-competition-outrights');
